import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Home, Send, CalendarClock, BadgeDollarSign } from "lucide-react";
import { api } from "@/lib/api";

// Canonical class values MUST match backend CLASS_OPTIONS exactly.
const CLASSES = [
  { value: "Spanish for Beginners - Monday 10:00-11:00 AM", type_en: "Spanish for Beginners", type_es: "Espa\u00F1ol para Principiantes", day_en: "Monday", day_es: "Lunes", time: "10:00\u201311:00 AM" },
  { value: "Spanish for Beginners - Wednesday 10:00-11:00 AM", type_en: "Spanish for Beginners", type_es: "Espa\u00F1ol para Principiantes", day_en: "Wednesday", day_es: "Mi\u00E9rcoles", time: "10:00\u201311:00 AM" },
  { value: "Spanish for Beginners - Wednesday 6:00-7:00 PM", type_en: "Spanish for Beginners", type_es: "Espa\u00F1ol para Principiantes", day_en: "Wednesday", day_es: "Mi\u00E9rcoles", time: "6:00\u20137:00 PM" },
  { value: "English for Beginners - Wednesday 7:15-8:15 PM", type_en: "English for Beginners", type_es: "Ingl\u00E9s para Principiantes", day_en: "Wednesday", day_es: "Mi\u00E9rcoles", time: "7:15\u20138:15 PM" },
  { value: "Spanish for Beginners - Thursday 10:00-11:00 AM", type_en: "Spanish for Beginners", type_es: "Espa\u00F1ol para Principiantes", day_en: "Thursday", day_es: "Jueves", time: "10:00\u201311:00 AM" },
  { value: "Bilingual Conversation Club - Thursday 6:30-7:30 PM", type_en: "Bilingual Conversation Club", type_es: "Club de Conversaci\u00F3n Biling\u00FCe", day_en: "Thursday", day_es: "Jueves", time: "6:30\u20137:30 PM" },
  { value: "English for Intermediates - Thursday 7:30-8:30 PM", type_en: "English for Intermediates", type_es: "Ingl\u00E9s Intermedio", day_en: "Thursday", day_es: "Jueves", time: "7:30\u20138:30 PM" },
];

const LEVELS = [
  { value: "", en: "Select (optional)", es: "Seleccionar (opcional)" },
  { value: "Complete beginner", en: "Complete beginner", es: "Principiante total" },
  { value: "Some experience", en: "Some experience", es: "Algo de experiencia" },
  { value: "Intermediate", en: "Intermediate", es: "Intermedio" },
  { value: "Not sure", en: "Not sure", es: "No estoy seguro/a" },
];

const T = {
  en: {
    switch: "Espa\u00F1ol",
    kicker: "Adult Classes",
    heroTitle: "Register for Our English & Spanish Classes",
    heroSub: "Join our welcoming, bilingual learning community in Oxford, PA. Pick a class time that works for you and we'll take care of the rest.",
    priceTitle: "$175 Monthly Membership",
    priceBody: "Your monthly membership includes the Bilingual Conversation Club.",
    formTitle: "Registration",
    nameLabel: "Full Name", emailLabel: "Email Address", phoneLabel: "Phone Number",
    classTitle: "Choose Your Class",
    classHint: "One class per registration. Need more than one? Submit the form again.",
    levelTitle: "Your Experience / Level",
    levelHint: "Optional \u2014 helps us place you well.",
    notesTitle: "Notes or Questions",
    notesHint: "Optional",
    consent: "I agree to be contacted about my registration, class confirmation, and payment.",
    submit: "Submit Registration", submitting: "Submitting...",
    errRequired: "Please complete all required fields, choose a class, and agree to be contacted.",
    errEmail: "Please enter a valid email address.",
    errGeneric: "Something went wrong. Please try again.",
    successTitle: "Thank you for registering!",
    successBody: "We've received your registration. Your spot is not reserved until payment is received \u2014 we'll contact you shortly to confirm your class and send a PayPal invoice. Questions? Call 856-344-3497.",
    successWaitTitle: "You're on the waitlist!",
    successWaitBody: "This class is currently full, so we've added you to the waitlist. We'll email you as soon as a spot opens \u2014 no payment is needed while you wait. Questions? Call 856-344-3497.",
    full: "Full", waitlistNote: "This class is full. You'll be added to the waitlist.",
    submitWaitlist: "Join the Waitlist",
    spotsLeft: (n) => `${n} ${n === 1 ? "spot" : "spots"} left`,
    returnHome: "Return to Home Page",
  },
  es: {
    switch: "English",
    kicker: "Clases para Adultos",
    heroTitle: "Inscr\u00EDbase en Nuestras Clases de Ingl\u00E9s y Espa\u00F1ol",
    heroSub: "\u00DAnase a nuestra acogedora comunidad de aprendizaje biling\u00FCe en Oxford, PA. Elija un horario que le convenga y nosotros nos encargamos del resto.",
    priceTitle: "Membres\u00EDa Mensual de $175",
    priceBody: "Su membres\u00EDa mensual incluye el Club de Conversaci\u00F3n Biling\u00FCe.",
    formTitle: "Inscripci\u00F3n",
    nameLabel: "Nombre Completo", emailLabel: "Correo Electr\u00F3nico", phoneLabel: "N\u00FAmero de Tel\u00E9fono",
    classTitle: "Elija Su Clase",
    classHint: "Una clase por inscripci\u00F3n. \u00BFNecesita m\u00E1s de una? Env\u00EDe el formulario nuevamente.",
    levelTitle: "Su Experiencia / Nivel",
    levelHint: "Opcional \u2014 nos ayuda a ubicarlo/a mejor.",
    notesTitle: "Notas o Preguntas",
    notesHint: "Opcional",
    consent: "Acepto que me contacten sobre mi inscripci\u00F3n, la confirmaci\u00F3n de la clase y el pago.",
    submit: "Enviar Inscripci\u00F3n", submitting: "Enviando...",
    errRequired: "Por favor complete los campos requeridos, elija una clase y acepte ser contactado/a.",
    errEmail: "Por favor ingrese un correo electr\u00F3nico v\u00E1lido.",
    errGeneric: "Algo sali\u00F3 mal. Int\u00E9ntelo de nuevo.",
    successTitle: "\u00A1Gracias por inscribirse!",
    successBody: "Hemos recibido su inscripci\u00F3n. Su lugar no est\u00E1 reservado hasta recibir el pago \u2014 nos comunicaremos con usted pronto para confirmar su clase y enviar una factura de PayPal. \u00BFPreguntas? Llame al 856-344-3497.",
    successWaitTitle: "\u00A1Est\u00E1 en la lista de espera!",
    successWaitBody: "Esta clase est\u00E1 llena por el momento, as\u00ED que lo/la agregamos a la lista de espera. Le enviaremos un correo en cuanto se abra un lugar \u2014 no se necesita pago mientras espera. \u00BFPreguntas? Llame al 856-344-3497.",
    full: "Lleno", waitlistNote: "Esta clase est\u00E1 llena. Se le agregar\u00E1 a la lista de espera.",
    submitWaitlist: "Unirse a la Lista de Espera",
    spotsLeft: (n) => `${n} ${n === 1 ? "lugar disponible" : "lugares disponibles"}`,
    returnHome: "Volver a la P\u00E1gina Principal",
  },
};

const inputCls = "w-full bg-white rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:ring-2 focus:ring-brand focus:border-transparent";
const lbl = "block text-sm font-semibold text-navy mb-2";

export default function Classes() {
  const [lang, setLang] = useState("en");
  const tr = T[lang];
  useEffect(() => { document.title = "Adult Class Registration | Alma y Lengua"; }, []);

  const [d, setD] = useState({ full_name: "", email: "", phone: "", level: "", notes: "", consent: false });
  const [classSelected, setClassSelected] = useState("");
  const [status, setStatus] = useState("idle");
  const [showErr, setShowErr] = useState(false);
  const [avail, setAvail] = useState({});
  const [waitlisted, setWaitlisted] = useState(false);

  useEffect(() => {
    api.get("/classes/availability")
      .then(({ data }) => { const m = {}; data.forEach((x) => { m[x.class_selected] = x; }); setAvail(m); })
      .catch(() => {});
  }, []);

  const selectedFull = !!(classSelected && avail[classSelected]?.full);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
  const valid = d.full_name.trim() && emailValid && d.phone.trim() && classSelected && d.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setShowErr(true); document.getElementById("classes-form").scrollIntoView({ behavior: "smooth" }); return; }
    setStatus("sending");
    try {
      const { data } = await api.post("/classes", { ...d, full_name: d.full_name.trim(), phone: d.phone.trim(), class_selected: classSelected });
      setWaitlisted(!!data.waitlisted);
      setStatus("success"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { setStatus("error"); }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen grid place-items-center px-5 py-16 bg-offwhite">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "backOut" }}
          className="max-w-xl w-full rounded-3xl shadow-2xl p-8 md:p-12 text-center bg-white border border-navy/5" data-testid="classes-success">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-6 bg-brand text-white"><CheckCircle2 size={44} /></motion.div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold mb-4 text-navy" data-testid="classes-success-title">{waitlisted ? tr.successWaitTitle : tr.successTitle}</h1>
          <p className="text-ink/70 leading-relaxed mb-8">{waitlisted ? tr.successWaitBody : tr.successBody}</p>
          <Link to="/" data-testid="classes-return-home" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white bg-navy hover:bg-brand transition-colors"><Home size={18} /> {tr.returnHome}</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite grain">
      {/* Lang toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button onClick={() => setLang(lang === "en" ? "es" : "en")} data-testid="classes-lang" className="rounded-full px-4 py-2 text-sm font-bold text-white shadow-lg bg-navy">{tr.switch}</button>
      </div>

      {/* Hero */}
      <section className="bg-navy text-white grain">
        <div className="max-w-4xl mx-auto px-5 py-16 md:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6 bg-gold text-navy">{tr.kicker}</span>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold leading-tight mb-5">{tr.heroTitle}</h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">{tr.heroSub}</p>
          </motion.div>
        </div>
      </section>

      {/* Pricing banner */}
      <section className="max-w-2xl mx-auto px-5 -mt-8 relative z-10">
        <Link to="/conversation-club" data-testid="classes-club-cta"
          className="block bg-navy rounded-3xl shadow-xl border border-navy/5 p-6 mb-4 text-center hover:bg-brand transition-colors group">
          <div className="text-gold text-xs font-bold uppercase tracking-widest mb-1">{lang === "es" ? "Todos los jueves · 6:30–7:30 PM" : "Every Thursday · 6:30–7:30 PM"}</div>
          <div className="font-heading text-xl md:text-2xl font-extrabold text-white mb-2">{lang === "es" ? "Club de Conversación en Español" : "Spanish Conversation Club"}</div>
          <span className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-extrabold text-navy bg-gold group-hover:bg-white transition-colors">
            <CalendarClock size={18} /> {lang === "es" ? "Inscríbete al Club de Conversación" : "Register for Conversation Club"}
          </span>
        </Link>
        <div className="bg-white rounded-3xl shadow-xl border border-navy/5 p-6 md:p-8 flex items-start gap-4" data-testid="classes-pricing">
          <span className="w-12 h-12 shrink-0 rounded-2xl grid place-items-center bg-aqua text-brand"><BadgeDollarSign size={24} /></span>
          <div>
            <h2 className="font-heading font-extrabold text-xl text-navy">{tr.priceTitle}</h2>
            <p className="text-ink/70 mt-1">{tr.priceBody}</p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="classes-form" className="max-w-2xl mx-auto px-5 py-12">
        <h2 className="font-heading text-3xl font-extrabold text-center mb-8 text-navy">{tr.formTitle}</h2>
        {showErr && !valid && <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-5 py-4 text-sm" data-testid="classes-error">{tr.errRequired}</div>}
        <form onSubmit={submit} noValidate className="space-y-6" data-testid="classes-form-el">
          {/* Contact */}
          <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-6 md:p-8 space-y-4">
            <div><label className={lbl}>{tr.nameLabel} *</label><input data-testid="classes-name" className={inputCls} value={d.full_name} onChange={(e) => setD({ ...d, full_name: e.target.value })} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>{tr.emailLabel} *</label><input type="email" data-testid="classes-email" className={inputCls} value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />{showErr && !emailValid && <p className="text-xs text-red-500 mt-1">{tr.errEmail}</p>}</div>
              <div><label className={lbl}>{tr.phoneLabel} *</label><input type="tel" data-testid="classes-phone" className={inputCls} value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} /></div>
            </div>
          </div>

          {/* Class selection */}
          <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-2 mb-1"><CalendarClock size={20} className="text-brand" /><h3 className="font-heading font-bold text-lg text-navy">{tr.classTitle} *</h3></div>
            <p className="text-sm text-ink/50 mb-5">{tr.classHint}</p>
            <div className="space-y-3" data-testid="classes-options">
              {CLASSES.map((c) => {
                const active = classSelected === c.value;
                const a = avail[c.value];
                const isFull = !!a?.full;
                return (
                  <label key={c.value} data-testid={`class-option-${c.value}`}
                    className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition ${active ? "border-brand bg-aqua/40" : "border-slate-200 bg-white hover:border-brand/40"}`}>
                    <input type="radio" name="class" checked={active} onChange={() => setClassSelected(c.value)} className="mt-1 w-5 h-5 accent-[#2E7D74]" />
                    <span className="flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-navy">{lang === "en" ? c.type_en : c.type_es}</span>
                        {isFull
                          ? <span className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 bg-red-100 text-red-600" data-testid={`class-badge-full-${c.value}`}>{tr.full}</span>
                          : (a && a.spots_left <= 5 && <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5 bg-gold/25 text-navy">{tr.spotsLeft(a.spots_left)}</span>)}
                      </span>
                      <span className="text-sm text-ink/60">{lang === "en" ? c.day_en : c.day_es} &middot; {c.time}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {selectedFull && <p className="mt-4 text-sm font-semibold text-red-600" data-testid="classes-waitlist-note">{tr.waitlistNote}</p>}
          </div>

          {/* Level */}
          <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-6 md:p-8">
            <label className={lbl}>{tr.levelTitle}</label>
            <p className="text-sm text-ink/50 mb-3">{tr.levelHint}</p>
            <select data-testid="classes-level" className={inputCls} value={d.level} onChange={(e) => setD({ ...d, level: e.target.value })}>
              {LEVELS.map((l) => <option key={l.value} value={l.value}>{lang === "en" ? l.en : l.es}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-6 md:p-8">
            <label className={lbl}>{tr.notesTitle} <span className="font-normal text-slate-400">{tr.notesHint}</span></label>
            <textarea data-testid="classes-notes" rows={4} className={inputCls} value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} />
          </div>

          {/* Consent + submit */}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="classes-consent">
            <input type="checkbox" checked={d.consent} onChange={(e) => setD({ ...d, consent: e.target.checked })} className="mt-1 w-5 h-5 accent-[#0F2D52]" />
            <span className="text-sm font-medium text-ink/80">{tr.consent} *</span>
          </label>
          {status === "error" && <p className="text-red-500 text-center">{tr.errGeneric}</p>}
          <button type="submit" data-testid="classes-submit" disabled={status === "sending"} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-5 font-extrabold text-white text-lg shadow-xl disabled:opacity-60 bg-navy hover:bg-brand transition-colors">
            <Send size={20} /> {status === "sending" ? tr.submitting : (selectedFull ? tr.submitWaitlist : tr.submit)}
          </button>
        </form>
      </section>
    </div>
  );
}
